(() => {
  if (customElements.get('ab-scratch-signup')) return;

  class ABScratchSignup extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.launcher = this.querySelector('[data-open]');
      this.storageKey = 'ab-family-scratch-v1';
      this.state = this.readState();
      this.listen(this.launcher, 'click', () => this.open());
      const forced = new URLSearchParams(location.search).get('ab_scratch_preview') === '1';
      const editor = Boolean(window.Shopify?.designMode);
      if (forced || (editor && this.dataset.editorPreview === 'true')) {
        this.timer = setTimeout(() => this.open(), 500);
      } else if (this.dataset.subscribed !== 'true' && !this.state.subscribed && !editor) {
        this.launcher.hidden = false;
        if (!(this.state.until > Date.now())) this.timer = setTimeout(() => this.autoOpen(), Number(this.dataset.delay) * 1000);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.revealTimer);
      this.abort?.abort();
      this.requestAbort?.abort();
      if (this.dialog?.open) this.close();
    }

    listen(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.abort.signal });
    }

    readState() {
      try { return JSON.parse(localStorage.getItem(this.storageKey)) || {}; }
      catch { return {}; }
    }

    saveState(changes) {
      this.state = { ...this.state, ...changes };
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch { /* Storage is optional. */ }
    }

    autoOpen() {
      if (!this.isConnected || this.state.subscribed || this.state.until > Date.now()) return;
      const occupied = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => {
        if (element === this.dialog || element.getAttribute('aria-hidden') === 'true') return false;
        const bounds = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return bounds.width > 0 && bounds.height > 0 && bounds.right > 0 && bounds.bottom > 0 && bounds.left < innerWidth && bounds.top < innerHeight && style.visibility !== 'hidden' && Number(style.opacity) > 0;
      });
      if (document.hidden || occupied || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) {
        this.timer = setTimeout(() => this.autoOpen(), 10000);
        return;
      }
      this.open();
    }

    open() {
      clearTimeout(this.timer);
      if (!this.dialog) this.mount();
      if (this.dialog.open || !this.dialog.showModal) return;
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.launcher.hidden = true;
      this.saveState({ until: Date.now() + Number(this.dataset.dismissDays) * 86400000 });
    }

    mount() {
      this.append(this.querySelector('template').content.cloneNode(true));
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.card = this.querySelector('[data-card]');
      this.intro = this.querySelector('[data-intro]');
      this.signup = this.querySelector('[data-signup]');
      this.success = this.querySelector('[data-success]');
      this.form = this.querySelector('form');
      this.querySelectorAll('[data-close]').forEach(button => this.listen(button, 'click', () => this.close()));
      this.listen(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      this.listen(this.dialog, 'click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.close();
      });
      this.listen(this.querySelector('[data-reveal]'), 'click', () => this.reveal());
      this.listen(this.form, 'submit', event => this.subscribe(event));
      this.listen(this.querySelector('[data-copy]'), 'click', async () => {
        try {
          await navigator.clipboard.writeText(this.querySelector('[data-code]').textContent.trim());
          this.querySelector('[data-copy-status]').textContent = this.dataset.copied;
        } catch {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(this.querySelector('[data-code]'));
          selection.removeAllRanges(); selection.addRange(range);
        }
      });
      this.paint();
      this.listen(this.canvas, 'pointerdown', event => {
        if (this.revealed || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault();
        this.pointerId = event.pointerId;
        this.canvas.setPointerCapture(event.pointerId);
        this.lastPoint = this.point(event);
        this.erase(this.lastPoint, this.lastPoint);
      });
      this.listen(this.canvas, 'pointermove', event => {
        if (event.pointerId !== this.pointerId || this.revealed) return;
        const point = this.point(event);
        this.erase(this.lastPoint, point);
        this.lastPoint = point;
        this.checkProgress();
      });
      const stop = event => {
        if (event.pointerId !== this.pointerId) return;
        if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
        this.pointerId = null;
        this.checkProgress(true);
      };
      this.listen(this.canvas, 'pointerup', stop);
      this.listen(this.canvas, 'pointercancel', stop);
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.launcher.hidden = this.state.subscribed || this.dataset.subscribed === 'true';
      if (this.previousFocus?.isConnected && this.previousFocus !== document.body) this.previousFocus.focus({ preventScroll: true });
      else if (!this.launcher.hidden) this.launcher.focus({ preventScroll: true });
    }

    paint() {
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!this.ctx) return;
      const ctx = this.ctx;
      const { width, height } = this.canvas;
      ctx.fillStyle = this.dataset.coating || '#c6aa64'; ctx.fillRect(0, 0, width, height);
      const glow = ctx.createLinearGradient(0, 0, width, height);
      glow.addColorStop(0, '#fff3c766'); glow.addColorStop(.3, '#fff3c708');
      glow.addColorStop(.48, '#fff8dc55'); glow.addColorStop(.64, '#fff3c700'); glow.addColorStop(1, '#67420e40');
      ctx.fillStyle = glow; ctx.fillRect(0, 0, width, height);
      let seed = 17;
      const random = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
      for (let i = 0; i < 20000; i++) {
        ctx.fillStyle = random() > .5 ? '#fff5d72b' : '#5b390e18';
        ctx.fillRect(random() * width, random() * height, 1.5, 1.5);
      }
      ctx.save(); ctx.translate(width / 2, height / 2); ctx.rotate(-.19);
      ctx.strokeStyle = '#79531d25'; ctx.lineWidth = 30; ctx.lineCap = 'butt';
      for (let x = -110; x <= 110; x += 42) {
        ctx.beginPath(); ctx.moveTo(x - 32, 75); ctx.lineTo(x + 32, -75); ctx.stroke();
      }
      ctx.restore();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#35250f'; ctx.font = '700 60px Arial, sans-serif';
      ctx.fillText(this.dataset.scratchLabel, width / 2, height / 2 - 8, width - 60);
      ctx.font = '400 20px Arial, sans-serif';
      ctx.fillText(this.dataset.scratchCaption, width / 2, height / 2 + 43, width - 72);
    }

    point(event) {
      const bounds = this.canvas.getBoundingClientRect();
      return { x: (event.clientX - bounds.left) * this.canvas.width / bounds.width, y: (event.clientY - bounds.top) * this.canvas.height / bounds.height };
    }

    erase(from, to) {
      if (!this.ctx) return;
      const ctx = this.ctx;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 62; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(to.x, to.y, 31, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }

    checkProgress(force = false) {
      if (!this.ctx || this.revealed || (!force && performance.now() - (this.lastCheck || 0) < 100)) return;
      this.lastCheck = performance.now();
      const { width, height } = this.canvas;
      const pixels = this.ctx.getImageData(0, 0, width, height).data;
      let clear = 0; let total = 0;
      for (let y = 10; y < height; y += 15) {
        for (let x = 10; x < width; x += 15) { total++; if (pixels[(y * width + x) * 4 + 3] < 100) clear++; }
      }
      if (clear / total >= .36) this.reveal();
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.card.classList.add('is-revealed');
      this.revealTimer = setTimeout(() => {
        this.intro.hidden = true;
        this.signup.hidden = false;
        if (this.dialog.open) this.querySelector('[data-reward-title]').focus({ preventScroll: true });
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650);
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      const email = this.form.elements.email.value.trim();
      const button = this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-error-message]');
      const originalLabel = button.innerHTML;
      this.busy = true; button.disabled = true; button.textContent = this.dataset.busy; error.hidden = true;
      this.requestAbort = new AbortController();
      const timeout = setTimeout(() => this.requestAbort.abort(), 20000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.company)}`, {
          method: 'POST', credentials: 'omit', signal: this.requestAbort.signal,
          headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-10-15' },
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: { custom_source: 'AB Family | Scratch signup', profile: { data: { type: 'profile', attributes: {
              email,
              properties: { '$source': 'AB Family | Scratch signup', AB_signup_experience: 'scratch' },
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
            } } } },
            relationships: { list: { data: { type: 'list', id: this.dataset.list } } }
          } })
        });
        if (!response.ok) throw new Error(response.status === 429 ? 'rate' : 'request');
        this.saveState({ subscribed: true });
        this.signup.hidden = true; this.success.hidden = false;
        this.form.reset();
        if (this.dialog.open) this.querySelector('[data-success-title]').focus({ preventScroll: true });
      } catch (failure) {
        error.textContent = failure.message === 'rate' ? this.dataset.rateError : this.dataset.error;
        error.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.busy = false; button.disabled = false; button.innerHTML = originalLabel;
      }
    }
  }
  customElements.define('ab-scratch-signup', ABScratchSignup);
})();

